/* 

 

   ───────────────────── BMP Selection Controls Component ─────────────────────── 
 



*/

import { useState, useEffect } from "react";
import { useAppStateContext } from '../../hooks/useAppState';

const API_BASE = process.env.API_BASE ?? "";



// ----------------------------------------------------------------------------------------
// Label mappings for display

// bmptype and individual are the ways we tell the lambda function the type of analysis we are doing
// Of course, its not the prettiest to look at, so to translate, we have this object and helper function
// TBH Not sure why the helper function is there. Its some vibe code
// But i'm pretty sure claude or GPT or whoever was just making it so if we ever introduce another analysis type,
//    even if its not added to the ANALYSIS_TYPE_LABELS object, its still safe and the code doesnt break
const ANALYSIS_TYPE_LABELS = {
  'bmptype': 'BMP Type',
  'individual': 'Individual BMP'
  // TEMPORARY: Treatment Trains temporarily disabled since no agency monitors one.
  //, 'treatmenttrain': 'Treatment Train'
};

// Helper function to get analysis type display label
const getDisplayLabel = (value, labelMap) => {
  return labelMap[value] || value;
};
// ----------------------------------------------------------------------------------------


/* 
    A Bit of background is that they can analyze data per 
        individual BMP's, 
        they can analyze entire sets of BMP Types, 
        or they can analyze special BMPs called Treatment Trains
*/


// This component handles the common BMP selection parameters that are shared between WQ and Hydrology tabs
export default function BMPSelectionControls() {

  // Get what we need from the context provider
  const { bmpState, updateBmpState } = useAppStateContext();

  const [sitenames, setSitenames]   = useState([]);
  const [bmpnames,  setBmpnames]    = useState([]);
  const [bmptypes,  setBmptypes]    = useState([]);
  const [error,     setError]       = useState(null);

  // Extract values from context
  const { analysistype, bmptype, sitename, bmpname } = bmpState;

  // TEMPORARY - disable treatment trains
  const analysistypes = {
    individual: "Individual BMPs",
    bmptype: "BMP Type"
    //, treatmenttrain: "Treatment Train"
  };



  // Helper function to get BMP type display name
  const getBmpTypeDisplayName = (bmptypeValue) => {
    const bmpTypeObj = bmptypes.find(bmp => bmp.bmptype === bmptypeValue);
    return bmpTypeObj?.bmptypedescription || bmptypeValue;
  };

  /* ── handlers for form changes ──────────────────────────────────────────────── */
  const handleSitenameChange = (e) => {
    updateBmpState({ 
      sitename: e.target.value,
      bmpname: "",
      selectedBmpType: ""
    });
  };

  const handleBmpnameChange = (e) => {
    const selectedOption = e.target.options[e.target.selectedIndex];
    
    // stored the type of BMP That it is in the dataset
    // the BMP Names api route returns the bmpnames and the types of BMPs that they are
    const selectedBmpType = selectedOption.dataset.bmptype; 
    
    // Update both BMP name and type in the main state
    updateBmpState({ 
      bmpname: e.target.value,
      selectedBmpType: selectedBmpType 
    });
  };

  const handleAnalysistypeChange = (e) => {
    const newAnalysistype = e.target.value;
    // Clear irrelevant fields when analysis type changes
    updateBmpState({ 
      analysistype: newAnalysistype,
      sitename: "",
      bmpname: "",
      selectedBmpType: "",
      bmptype: ""
    });
  };

  const handleBmptypeChange = (e) => {
    updateBmpState({ bmptype: e.target.value });
  };

  // Helper function to handle API responses
  const handleApiResponse = async (res) => {
    const data = await res.json();
    
    if (!res.ok) {
      // Handle 422 errors specifically with user-friendly messages
      if (res.status === 422) {
        throw new Error(data.error || "No data found for the selected parameters. Please check your selections and try again.");
      }
      
      throw new Error(`HTTP ${res.status}: ${data.error || data.message || 'Unknown error'}`);
    }
    
    return data;
  };

  /* ── API calls based on analysistype ────────────────────────────────────────── */

  // Fetch sitenames for individual and treatmenttrain analysis types
  useEffect(() => {
    if (analysistype === "individual" || analysistype === "treatmenttrain") {
      setError(null);
      fetch(`${API_BASE}/selectioncontrols/wq/${analysistype}/sitenames`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}"
      })
        .then(handleApiResponse)
        .then(data => {

            // Sort site names numerically by the number at the end
            let tmpSiteNames = data.sitenames;
            tmpSiteNames.sort((a, b) => {
              const numA = parseInt(a?.match(/\d+$/)?.[0] || 0);
              const numB = parseInt(b?.match(/\d+$/)?.[0] || 0);
              return numA - numB;
            });
            setSitenames(tmpSiteNames);
            
        })
        .catch(e => {
          setError(e.message);
        });
    } else {
      setSitenames([]);
    }
  }, [analysistype]);



  // Fetch bmptypes for bmptype analysis
  useEffect(() => {
    if (analysistype === "bmptype") {
      setError(null);

      fetch(`${API_BASE}/selectioncontrols/wq/${analysistype}/bmptypes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}"
      })
        .then(handleApiResponse)
        .then(data => {
          setBmptypes(data.bmptypes);
        })
        .catch(e => {
          setError(e.message);
        });
    } else {
      setBmptypes([]);
    }
  }, [analysistype]);




  // Fetch bmpnames based on analysistype
  useEffect(() => {
    if (!analysistype) {
      setBmpnames([]);
      return;
    }

    setBmpnames([]);
    setError(null);

    let body = {};
    let endpoint = "";

    if (analysistype === "individual" && sitename) {
      endpoint = `${API_BASE}/selectioncontrols/wq/${analysistype}/bmpnames`;
      body = { sitename };
    } else if (analysistype === "bmptype" && bmptype) {
      endpoint = `${API_BASE}/selectioncontrols/wq/${analysistype}/bmpnames`;
      body = { bmptype };
    } else if (analysistype === "treatmenttrain" && sitename) {
      endpoint = `${API_BASE}/selectioncontrols/wq/${analysistype}/bmpnames`;
      body = { sitename, first_or_last: "first" }; // Default to first BMP in treatment train
    }

    if (endpoint) {
      fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      })
        .then(handleApiResponse)
        .then(data => {

          // Sort BMP names numerically by the number at the end
          let tmpBmpNames = data.bmpnames;
          tmpBmpNames.sort((a, b) => {
            // a and b are objects with bmpname property
            const numA = parseInt(a?.bmpname?.match(/\d+$/)?.[0] || 0);
            const numB = parseInt(b?.bmpname?.match(/\d+$/)?.[0] || 0);
            return numA - numB;
          });
          setBmpnames(tmpBmpNames);

        })
        .catch(e => {
          setError(e.message);
        });
    }
  }, [sitename, bmptype, analysistype]);





  return (
    <div className="flex justify-center items-start w-full mt-8">
      <div className="w-full max-w-4xl bg-white rounded-xl shadow p-0 border border-gray-200">
        {/* Controls Section */}
        <div className="border-b px-6 py-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Analysis Type */}
          <div>
            <label className="block text-sm font-medium mb-1">Analysis Type</label>
            <select
              className="w-full border rounded px-2 py-1"
              value={analysistype}
              onChange={handleAnalysistypeChange}
            >
              <option value="">Select analysis type…</option>
              {Object.keys(analysistypes).map(key => (
                <option key={key} value={key}>{analysistypes[key]}</option>
              ))}
            </select>
          </div>




          {/* Site Name or BMP Type */}
          {analysistype === "individual" && (
            <div>
              <label className="block text-sm font-medium mb-1">Site Name</label>
              <select
                className="w-full border rounded px-2 py-1"
                value={sitename}
                onChange={handleSitenameChange}
              >
                <option value="">Select a site…</option>
                {sitenames.map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          )}

          {analysistype === "bmptype" && (
            <div>
              <label className="block text-sm font-medium mb-1">BMP Type</label>
              <select
                className="w-full border rounded px-2 py-1"
                value={bmptype}
                onChange={handleBmptypeChange}
              >
                <option value="">Select a BMP type…</option>
                {bmptypes.map(bmp => (
                  <option key={bmp.bmptype} value={bmp.bmptype}>
                    {bmp.bmptypedescription || bmp.bmptype}
                  </option>
                ))}
              </select>
            </div>
          )}




          {
            /* 
                Elizabeth said treatment trains aren't going to be analyzed - thankfully. 
                But we can keep this treatment train stuff around in case anyone changes their mind 
            */
          }
          {analysistype === "treatmenttrain" && (
            <div>
              <label className="block text-sm font-medium mb-1">Site Name</label>
              <select
                className="w-full border rounded px-2 py-1"
                value={sitename}
                onChange={handleSitenameChange}
              >
                <option value="">Select a site…</option>
                {sitenames.map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          )}




          {/* Select BMP Name (only for individual) */}
          {analysistype === "individual" && (
            <div>
              <label className="block text-sm font-medium mb-1">BMP Name</label>
              <select
                className="w-full border rounded px-2 py-1"
                value={bmpname}
                disabled={!bmpnames.length}
                onChange={handleBmpnameChange}
              >
                <option value="">Select a BMP…</option>
                {bmpnames.map(n => (
                  <option key={n.bmpname} value={n.bmpname} data-bmptype={n.bmptype} className="bmp-option">{n.bmpname}</option>
                ))}
              </select>
            </div>
          )}
        </div>




        {/* Status Section (Just showing what they currently have selected */}
        <div className="px-6 py-4">
          <h3 className="text-md font-semibold mb-2">Current Selection:</h3>
          <div className="text-sm text-gray-600">
            <p><strong>Analysis Type:</strong> {getDisplayLabel(analysistype, ANALYSIS_TYPE_LABELS) || 'Not selected'}</p>
            {analysistype === "individual" && (
              <>
                <p><strong>Site Name:</strong> {sitename || 'Not selected'}</p>
                <p><strong>BMP Name:</strong> {bmpname || 'Not selected'}</p>
              </>
            )}
            {analysistype === "bmptype" && (
              <p><strong>BMP Type:</strong> {getBmpTypeDisplayName(bmptype) || 'Not selected'}</p>
            )}
            {analysistype === "treatmenttrain" && (
              <p><strong>Site Name:</strong> {sitename || 'Not selected'}</p>
            )}
          </div>
        </div>



        {error && <p className="text-red-600 text-sm px-6 pb-4">{error}</p>}


      </div>


    </div>
  );
} 